// Verbatim from docs/spec/modules/reports-other.md and the live notes (reports-weekly, reports-shared, reports-nav).
export const s = {
  pageTitle: "Reports",
  auditTitle: "Audit log",
  families: { TIME: "Time report", TEAM: "Team report", EXPENSE: "Expense report" },
  familyToggle: { TIME: "TIME REPORT", TEAM: "TEAM REPORT", EXPENSE: "EXPENSE REPORT" },
  tabs: {
    summary: "Summary",
    detailed: "Detailed",
    weekly: "Weekly",
    shared: "Shared",
    attendance: "Attendance",
    assignments: "Assignments",
    expenseDetailed: "Detailed",
  },
  reportTabs: "Report tabs",
  export: "EXPORT",
  exportMenu: "Export",
  savePdf: "Save as PDF",
  saveCsv: "Save as CSV",
  saveExcel: "Save as Excel",
  customization: "Customization",
  filter: "FILTER",
  applyFilter: "APPLY FILTER",
  team: "Team",
  client: "Client",
  project: "Project",
  task: "Task",
  tag: "Tag",
  description: "Description",
  descriptionPlaceholder: "Enter description",
  withoutDescription: "Without description",
  searchTeam: "Search users or groups",
  searchClient: "Search client",
  searchProject: "Search project",
  searchTag: "Search tag",
  groups: "Groups",
  users: "Users",
  total: "Total:",
  billable: "Billable:",
  createInvoice: "Create invoice",
  print: "Print",
  share: "Share",
  rounding: "Rounding",
  groupBy: "Group by:",
  groupByValue: (v: string) => `Group by: ${v}`,
  showTime: "Show time",
  showTimeOptions: { TIME: "Time", EARNED: "Amount", COST: "Cost", PROFIT: "Profit" },
  projectCol: "PROJECT",
  userCol: "USER",
  withoutProject: "Without project",
  expandAll: "Expand all",
  collapseAll: "Collapse all",
  expandRow: (name: string) => `Show details of ${name}`,
  showUsersWithoutTime: "Show users without time",
  hideUsersWithoutTime: "Hide users without time",
  emptyTitle: "No data for the selected period",
  emptyText: "Try changing the date range or filters.",
  loadError: "Failed to load report",
  // Share modal (spec 4.1)
  shareTitle: "Share report",
  reportName: "Report name",
  reportNamePlaceholder: "Description",
  reportNameError: "Report name must be between 1 and 250 characters.",
  visibility: "Visibility",
  visibilityPublic: "Public - Anyone with the link can view",
  visibilityPrivate: "Private - Only selected users and groups can view",
  selectUsersGroups: "Select users and groups",
  alwaysOpenAs: (preset: string) => `Always open as ${preset.toLowerCase()}`,
  lockDates: "Lock dates",
  lockDatesTip: "Viewers won't be able to change the date range",
  scheduleToEmail: "Schedule to email",
  scheduleRelativeOnly: "Only reports with relative date range can be scheduled",
  recipients: "Recipients",
  selectUsers: "Select users",
  sendReport: "Send report",
  cadence: { DAILY: "Daily", WEEKLY: "Weekly", MONTHLY: "Monthly" },
  scheduleHelp: "Emails are sent according to users' personal time settings.",
  close: "Close",
  createLink: "CREATE LINK",
  save: "SAVE",
  copy: "Copy",
  linkCopied: "Link copied",
  shareLink: "Shared report link",
  editTitle: "Edit shared report",
  // Shared list (live reports-shared)
  allShared: "All shared",
  createdByMe: "Created by me",
  sharedWithMe: "Shared with me",
  searchByName: "Search by name",
  sharedReports: "SHARED REPORTS",
  nameCol: "NAME",
  labelCol: "LABEL",
  urlCol: "URL",
  public: "Public",
  private: "Private",
  scheduled: "Scheduled",
  noShared: "No shared reports",
  noSharedHint: "Try sharing summary, detailed or weekly report.",
  actions: "Actions",
  edit: "Edit",
  schedule: "Schedule",
  stopScheduling: "Stop scheduling",
  seeFilters: "See filters",
  filtersTitle: "Filters",
  delete: "Delete",
  deleteTitle: "Delete report",
  deleteBody: "Are you sure you want to delete this report? Link will be deactivated.",
  deleteConfirm: "DELETE",
  deleted: "Report deleted",
  saved: "Report saved",
  scheduleSaved: "Schedule saved",
  types: {
    SUMMARY: "Summary report",
    DETAILED: "Detailed report",
    WEEKLY: "Weekly report",
    ATTENDANCE: "Attendance report",
    EXPENSE_DETAILED: "Expense report",
  } as Record<string, string>,
  // Shared viewer (spec 4.3)
  unavailable: "This report is no longer available",
  noAccess: "You don't have access to this report",
  typeUnavailable: "This report type cannot be opened from a shared link yet",
  // Attendance (spec 6)
  start: "Start",
  end: "End",
  capacity: "Capacity",
  work: "Work",
  overtime: "Overtime",
  undertime: "Undertime",
  balance: "Balance",
  break: "Break",
  timeOff: "Time off",
  attendanceCols: {
    title: "TITLE",
    start: "START",
    end: "END",
    capacity: "CAPACITY",
    work: "WORK",
    overtime: "OVERTIME",
    undertime: "UNDERTIME",
    balance: "BALANCE",
    break: "BREAK",
    timeOff: "TIME OFF",
  },
  overtimeTip: (period: string) => `Overtime is calculated ${period.toLowerCase()} based on workspace settings`,
  undertimeTip: "Undertime is the capacity left after the tracked work",
  balanceTip: "Balance is overtime minus undertime",
  groupOptions: { USER: "User", DATE: "Date", WEEK: "Week", MONTH: "Month", NONE: "None" },
  comparator: { EXACTLY: "Exactly", LARGER_THAN: "Larger than", SMALLER_THAN: "Smaller than" },
  valueHours: "Value (hours)",
  valueTime: "Value (HH:mm)",
  clear: "Clear",
  // Assignments (spec 8)
  scheduledLabel: "Scheduled:",
  trackedLabel: "Tracked:",
  assignmentCols: {
    title: "TITLE",
    scheduled: "SCHEDULED",
    amount: "AMOUNT",
    cost: "COST",
    expectedProfit: "EXPECTED PROFIT",
    tracked: "TRACKED",
    realizedProfit: "REALIZED PROFIT",
    difference: "DIFFERENCE",
    amountDifference: "AMOUNT DIFFERENCE",
    costDifference: "COST DIFFERENCE",
    status: "STATUS",
  },
  assignmentGroups: {
    PROJECT: "Project",
    USER: "User",
    GROUP: "Group",
    CLIENT: "Client",
    TASK: "Task",
    MONTH: "Month",
    WEEK: "Week",
    DATE: "Date",
  } as Record<string, string>,
  groupLevel: (n: number) => `Group by level ${n}`,
  // Audit log (spec 9)
  author: "Author",
  action: "Action",
  system: "System",
  runReport: "RUN REPORT",
  exportCsv: "EXPORT AS CSV",
  timestampCol: "TIMESTAMP",
  authorCol: "AUTHOR",
  actionCol: "ACTION",
  property: "Property",
  previousValue: "Previous value",
  newValue: "New value",
  updated: "Updated",
  noActivity: "No activity for the selected filters",
  rangeLimit: "Date range must not exceed 30 days",
  searchAuthor: "Search users",
  searchAction: "Search actions",
  loadMore: "Load more",
  actionLabels: {
    CREATE_TIME_PERSONAL_TIMER: "Created time (timer)",
    CREATE_TIME_PERSONAL_MANUAL: "Created time (manual)",
    CREATE_TIME_IMPORT: "Created time (import)",
    CREATE_TIME_KIOSK: "Created time (kiosk)",
    CREATE_TIME_FOR_OTHER: "Created time (for others)",
    RESTORE_TIME: "Restored time (personal)",
    RESTORE_TIME_FOR_OTHER: "Restored time (for others)",
    UPDATE_TIME_PERSONAL: "Updated time (personal)",
    UPDATE_TIME_FOR_OTHER: "Updated time (for others)",
    DELETE_TIME_PERSONAL: "Deleted time (personal)",
    DELETE_TIME_FOR_OTHER: "Deleted time (for others)",
    CREATE_PROJECT: "Created project",
    CREATE_PROJECT_IMPORT: "Created project (import)",
    UPDATE_PROJECT: "Updated project",
    DELETE_PROJECT: "Deleted project",
    CREATE_TASK: "Created task",
    CREATE_TASK_IMPORT: "Created task (import)",
    UPDATE_TASK: "Updated task",
    DELETE_TASK: "Deleted task",
    CREATE_CLIENT: "Created client",
    CREATE_CLIENT_IMPORT: "Created client (import)",
    UPDATE_CLIENT: "Updated client",
    DELETE_CLIENT: "Deleted client",
    CREATE_TAG: "Created tag",
    CREATE_TAG_IMPORT: "Created tag (import)",
    UPDATE_TAG: "Updated tag",
    DELETE_TAG: "Deleted tag",
    CREATE_EXPENSE: "Created expense",
    CREATE_EXPENSE_FOR_OTHER: "Created expense (for others)",
    RESTORE_EXPENSE: "Restored expense",
    RESTORE_EXPENSE_FOR_OTHER: "Restored expense (for others)",
    UPDATE_EXPENSE: "Updated expense",
    UPDATE_EXPENSE_FOR_OTHER: "Updated expense (for others)",
    DELETE_EXPENSE: "Deleted expense",
    DELETE_EXPENSE_FOR_OTHER: "Deleted expense (for others)",
  } as Record<string, string>,
  noDescription: "(no description)",
  yes: "Yes",
  no: "No",
} as const;
